import { motion } from "framer-motion";
import { BRAND, IMAGES, T } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon, Phone } from "./icons";
import { Stars } from "./Stars";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { lang, tr } = useLang();
  const { brand, reviews } = useSiteData();
  const lines = T.hero.lines[lang];
  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length) : 0;

  return (
    <section id="hero" className="relative min-h-[100svh] w-full overflow-hidden bg-[#0C0806]" data-testid="hero-section">
      <motion.div
        initial={{ scale: 1.25 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <img src={IMAGES.hero} alt="Slaly braided hairstyle" className="h-full w-full object-cover object-center opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C0806]/95 via-[#0C0806]/55 to-[#0C0806]/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0806] via-transparent to-[#0C0806]/60" />
      </motion.div>

      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-6 pb-24 pt-40 lg:px-10 lg:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.hero.eyebrow)}</span>
        </motion.div>

        <h1 className="font-serif text-[15vw] leading-[0.9] tracking-tight text-[#F5ECDA] sm:text-[13vw] lg:text-[9.5vw]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === lines.length - 1 ? "italic text-[#D4AF37]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-[#E9DFCB] lg:text-lg">{tr(T.hero.sub)}</p>
          <div className="flex flex-col gap-4">
            {reviews.length > 0 && (
              <a href="/avis" className="flex items-center gap-2.5" data-testid="hero-rating">
                <Stars value={Math.round(avg)} size={16} />
                <span className="font-serif text-lg text-[#F5ECDA]">{avg.toFixed(1)}</span>
                <span className="text-xs text-[#B7A78C]">· {reviews.length} {tr(T.reviews.count)}</span>
              </a>
            )}
            <div className="flex flex-wrap gap-3">
            <a
              href={`https://wa.me/${brand.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] px-7 py-4 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-[#F3E5AB]"
              data-testid="hero-whatsapp-cta"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {tr(T.nav.book)}
            </a>
            <a
              href={`tel:${brand.tel}`}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] backdrop-blur-sm transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              data-testid="hero-call-cta"
            >
              <Phone className="h-4 w-4" />
              {brand.phoneDisplay}
            </a>
          </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 lg:flex lg:right-10"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#B7A78C]">{tr(T.hero.scroll)}</span>
        <span className="scroll-line h-10 w-px bg-[#D4AF37]/60" />
      </motion.div>
    </section>
  );
};
